import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import React from "react";
import { AlertTriangle } from "lucide-react";

interface ConfirmationDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: React.ReactNode;
    description: React.ReactNode;
    confirmText?: string;
    cancelText?: string;
    isLoading?: boolean;
    type?: "danger" | "warning" | "info";
    documentHeight?: number;
}

const typeConfig = {
    danger: {
        iconColor: "#dc2626",
        confirmButtonSx: {
            bgcolor: "#dc2626",
            color: "#fff",
            "&:hover": { bgcolor: "#b91c1c" },
        },
    },
    warning: {
        iconColor: "#ca8a04",
        confirmButtonSx: {
            bgcolor: "#ca8a04",
            color: "#fff",
            "&:hover": { bgcolor: "#a16207" },
        },
    },
    info: {
        iconColor: "#2563eb",
        confirmButtonSx: {
            bgcolor: "#2563eb",
            color: "#fff",
            "&:hover": { bgcolor: "#1d4ed8" },
        },
    },
} as const;

export function ConfirmationDialog({
    isOpen,
    onClose,
    onConfirm,
    title,
    description,
    confirmText = "Confirm",
    cancelText = "Cancel",
    isLoading = false,
    type = "danger",
}: ConfirmationDialogProps) {
    const styles = typeConfig[type];

    return (
        <Dialog
            open={isOpen}
            onClose={(_, reason) => {
                if (isLoading) return;
                if (reason === "backdropClick" || reason === "escapeKeyDown") {
                    onClose();
                }
            }}
            fullWidth
            maxWidth="sm"
            scroll="paper"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 4,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <DialogTitle sx={{ px: 3, pt: 3, pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ color: styles.iconColor, display: "flex", alignItems: "center" }}>
                        <AlertTriangle className="h-6 w-6" />
                    </Box>
                    <Typography component="h2" variant="h6" sx={{ fontWeight: 600 }}>
                        {title}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 2 }}>
                <DialogContentText
                    component="p"
                    sx={{ color: "text.secondary", fontSize: 14, lineHeight: 1.6 }}
                >
                    {description}
                </DialogContentText>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1, flexDirection: { xs: "column-reverse", sm: "row" }, justifyContent: "flex-end" }}>
                <Button
                    onClick={onClose}
                    disabled={isLoading}
                    variant="outlined"
                    fullWidth
                    sx={{
                        borderRadius: 3,
                        textTransform: "none",
                        minWidth: { sm: 104 },
                    }}
                >
                    {cancelText}
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={isLoading}
                    variant="contained"
                    fullWidth
                    sx={{
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 700,
                        minWidth: { sm: 104 },
                        ...styles.confirmButtonSx,
                    }}
                >
                    {isLoading ? "Processing..." : confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
